import { Box } from "@chakra-ui/react"
import { Indicator } from "@pathwright/ux/src/components/button/IconButton"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import OptionsMenu, {
  OptionsMenuItem
} from "@pathwright/ux/src/components/menu/OptionsMenu"
import { memo } from "react"

import {
  NonArchivedDiscussionResponseFragment,
  useDiscussionContext
} from "../DiscussionProvider"
import {
  AskToRespond,
  useAskToRespondLazyRegistrations
} from "../list/item/DiscussionListItemControls"
import useDiscussionResponseHasUnsavedChanges from "./useDiscussionResponseHasUnsavedChanges"
import useDiscussionResponsePermissions from "./useDiscussionResponsePermissions"

function AskToRespondToResponse({
  response
}: {
  response: NonArchivedDiscussionResponseFragment
}) {
  const post = useDiscussionContext((c) => c.post)

  return (
    <AskToRespond discussion={post!} response={response}>
      {({ onClick, item }) => (
        <OptionsMenuItem onClick={onClick} closeOnClick={false}>
          {item}
        </OptionsMenuItem>
      )}
    </AskToRespond>
  )
}

type ResponseListItemMenuProps = {
  response: NonArchivedDiscussionResponseFragment
}

const ResponseListItemMenu = memo(({ response }: ResponseListItemMenuProps) => {
  const { t } = useTranslate()
  const cohortId = useDiscussionContext((c) => c.post?.cohort_id!)
  const setEditingResponse = useDiscussionContext((c) => c.setEditingResponse)
  const setDeletingResponse = useDiscussionContext((c) => c.setDeletingResponse)
  const permissions = useDiscussionResponsePermissions(cohortId!, response)

  function onEditDiscussion() {
    setEditingResponse(response)
  }

  function onDeleteDiscussion() {
    setDeletingResponse(response)
  }

  const hasUnsavedChanges = useDiscussionResponseHasUnsavedChanges({
    response
  })

  // Fetch registrations only if we can ask to answer and the menu is open.
  // We merely need to know the loading state of registrations used for rendering
  // the AskToRespond menu item. This is purely to improve UX, avoiding a layout shift
  // when the user opens the menu and the item appears after registrations load.
  const [loadRegistrations, { loading: registrationsLoading }] =
    useAskToRespondLazyRegistrations(cohortId!)

  return permissions.canUpdate ||
    permissions.canDelete ||
    permissions.canAskToAnswer ? (
    <Box position="relative">
      <OptionsMenu
        className="note-settings-menu"
        menuProps={{
          onOpenChange: (e: { open: boolean }) => {
            if (e.open && permissions.canAskToAnswer) {
              loadRegistrations()
            }
          },
          closeOnSelect: false
        }}
        isLoading={registrationsLoading}
        // triggerProps={{ "aria-label": "Note Settings", variant: "secondaryGray" }}
      >
        {!registrationsLoading && (
          <>
            {permissions.canUpdate && (
              <OptionsMenuItem
                icon="edit"
                label={t("discussion.actions.menu.edit")}
                onClick={onEditDiscussion}
                closeOnClick
              />
            )}
            {permissions.canDelete && (
              <OptionsMenuItem
                icon="trash"
                label={t("discussion.actions.menu.delete")}
                onClick={onDeleteDiscussion}
                closeOnClick
              />
            )}
            {permissions.canAskToAnswer && (
              <AskToRespondToResponse response={response} />
            )}
          </>
        )}
      </OptionsMenu>
      {/* NOTE: better UX might be to hide the indicator when the menu is open and
            render the indicator on the menu item */}
      {hasUnsavedChanges && <Indicator />}
    </Box>
  ) : null
})

export default ResponseListItemMenu
